// =====================================================================
// GroupsTab.jsx — Tab "Groupes" în pagina Utilisateurs (Day 9)
// =====================================================================
// Card grid cu toate grupurile + buton "+ Nouveau groupe".
// Click pe card → modal mare cu 8 secțiuni de parametrare.
//
// Permission gate: users.read.all (vizualizare), users.create.all (editare)
// =====================================================================

import { useState, useEffect, useMemo } from 'react';
import { groups as groupsApi, users as usersApi, permissions as permsApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { marqueurInactif } from '../../lib/tri-personnes.js';
import { ROLES_DEFAUT_EQUIPE as ROLES, libelleRole, ficheReservee } from '../../lib/roles.js';
import { DROITS_MODIFIER_EQUIPE, DROITS_CREER_EQUIPE, DROITS_REGLER_HS_EQUIPE } from '../../lib/droits-equipes.js';

const EMPLOYMENT_TYPES = [
  { value: 'PERMANENT',    label: 'Permanent',    badgeColor: '#dbeafe', textColor: '#1e40af' },
  { value: 'OCCASIONNEL',  label: 'Occasionnel',  badgeColor: '#fef3c7', textColor: '#78350f' },
  { value: 'ADMIN',        label: 'Admin',        badgeColor: '#ede9fe', textColor: '#5b21b6' },
  { value: 'SERVICE',      label: 'Service',      badgeColor: '#d1fae5', textColor: '#065f46' },
];

const SCHEDULE_PATTERNS = [
  { value: 'FULL_TIME',            label: 'Temps plein (lun-ven)' },
  { value: 'PART_TIME_MORNING',    label: 'Temps partiel matin' },
  { value: 'PART_TIME_AFTERNOON',  label: 'Temps partiel après-midi' },
  { value: 'VARIABLE',             label: 'Variable / Flexible' },
];


const LEAVE_TYPES = [
  { code: 'VA',    label: 'Vacances annuelles' },
  { code: 'VAN',   label: 'VA année suivante (avance)' },
  { code: 'VAEN1', label: 'VA reportées' },
  { code: 'AA',    label: 'Absences autorisées' },
  { code: 'REC',   label: 'Récupération (auto)' },
  { code: 'MAT',   label: 'Maternité' },
  { code: 'PAT',   label: 'Paternité' },
  { code: 'MAL',   label: 'Maladie' },
];

const MODULES = [
  { key: 'temps',         label: '⏱️ Temps & Présence' },
  { key: 'conges',        label: '🌴 Congés' },
  { key: 'trajets',       label: '🚚 Trajets' },
  { key: 'prelevements',  label: '📦 Prélèvements' },
  { key: 'impressions',   label: '🖨️ Impressions' },
  { key: 'inventaire',    label: '📊 Inventaire' },
  { key: 'palettes',      label: '🟦 Palettes' },
  { key: 'sav',           label: '🔧 SAV' },
  { key: 'evaluations',   label: '📈 Évaluations' },
];

const DEFAULT_GROUP = {
  name: '', description: '', employmentType: 'PERMANENT',
  color: '#6366f1', icon: '🏷️', isActive: true,
  managerUserId: null, defaultRole: 'OPERATOR',
  defaultContractHoursWeek: 38, schedulePattern: 'FULL_TIME',
  workDaysMask: 31, defaultStartTime: '08:00', defaultEndTime: '17:00',
  leaveDefaults: { VA: 25, VAN: 0, VAEN1: 0, AA: 10, REC: 0, MAT: 105, PAT: 20 },
  allowedLeaveTypes: ['VA', 'VAN', 'VAEN1', 'AA', 'REC', 'MAT', 'PAT', 'MAL'],
  modulesEnabled: { temps: true, conges: true, trajets: false, prelevements: false, impressions: false, inventaire: false, palettes: false, sav: false, evaluations: false },
  notifyManagerWeekly: false, notifyOvertimeCap: 'NONE', notifyTSReminder: false, leaveReminderDays: 3,
  overtimeSettings: { capHoursMonth: 40, warningThreshold: 35, resetPolicy: 'NEVER', defaultDecision: 'NONE' },
};

/**
 * Liste des comptes pour les listes déroulantes de la fiche d'équipe (identité
 * seulement). Seul un REFUS (403) est attendu — un lecteur avec les seuls droits
 * « équipes » : on renvoie alors `repli`. Une panne (500, réseau) remonte, pour
 * se voir, au lieu d'une liste vide qui ferait croire que personne n'existe.
 */
function listerComptes(repli) {
  return usersApi.list({ champs: 'minimal' }).catch((e) => {
    // Marquée « refusée » : l'écran dit alors pourquoi la liste est vide.
    if (e?.response?.status === 403) return Object.assign([...repli], { refusee: true });
    throw e;
  });
}

export default function GroupsTab() {
  const { can } = useAuth();
  // Trois pouvoirs distincts, enfin — chacun miroir EXACT de sa route :
  //   modifier une équipe (composition, réglages)  → PUT /groups/:id, membres
  //   créer une équipe                              → POST /groups
  //   donner des droits à une équipe                → PUT /groups/:id/permissions
  // Le dernier reste réservé : il écrit les mêmes lignes que l'écran Droits.
  const canEdit = can(DROITS_MODIFIER_EQUIPE);
  const canCreate = can(DROITS_CREER_EQUIPE);
  const canEditDroits = can('users.create.all');
  // Réglages d'heures sup. de l'équipe : même droit que le serveur (DROITS_REGLER_HS).
  const canEditHs = can(DROITS_REGLER_HS_EQUIPE);

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);

  const [editTarget, setEditTarget] = useState(null); // group object or 'new' for create

  async function loadGroups() {
    setLoading(true);
    setError(null);
    try {
      const list = await groupsApi.list({ includeArchived });
      setGroups(list);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadGroups(); }, [includeArchived]);

  const filtered = useMemo(() => {
    let list = [...groups];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((g) =>
        g.name?.toLowerCase().includes(q) ||
        g.description?.toLowerCase().includes(q) ||
        g.employmentType?.toLowerCase().includes(q)
      );
    }
    if (filterType) {
      list = list.filter((g) => g.employmentType === filterType);
    }
    return list;
  }, [groups, search, filterType]);

  if (loading) return <div style={{ padding: 32, color: '#64748b' }}>Chargement...</div>;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 16 }}>Groupes <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 13 }}>({filtered.length})</span></h2>
        {canCreate && (
          <button onClick={() => setEditTarget('new')} style={{ padding: '6px 14px', background: '#16a34a', color: 'white', border: 0, borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
            + Nouveau groupe
          </button>
        )}
      </div>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="🔍 Rechercher groupe..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={inputStyle}
        />
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={selectStyle}>
          <option value="">Tous les types</option>
          {EMPLOYMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Inclure archivés
        </label>
      </div>

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', color: '#991b1b', borderRadius: 6, marginBottom: 14 }}>{error}</div>
      )}

      {/* Cards grid */}
      {filtered.length === 0 ? (
        <div style={{ padding: 32, background: '#F0FDF4', border: '1px dashed #86EFAC', borderRadius: 8, textAlign: 'center', color: '#16a34a' }}>
          Aucun groupe ne correspond aux filtres.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {filtered.map((g) => {
            const empType = EMPLOYMENT_TYPES.find((t) => t.value === g.employmentType);
            return (
              <div
                key={g.id}
                onClick={() => setEditTarget(g)}
                style={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, cursor: 'pointer', transition: 'all 0.15s' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#6366f1'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(99,102,241,0.12)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                  <span style={{ width: 36, height: 36, borderRadius: 8, background: g.color || '#6366f1', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>
                    {g.icon || '🏷️'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{g.name}</div>
                    <div style={{ fontSize: 11, marginTop: 2 }}>
                      {empType && <span style={{ ...badgeStyle, background: empType.badgeColor, color: empType.textColor, marginRight: 4 }}>{empType.label}</span>}
                      <span style={{ ...badgeStyle, background: g.isActive ? '#dcfce7' : '#f1f5f9', color: g.isActive ? '#166534' : '#64748b' }}>{g.isActive ? 'Actif' : 'Archivé'}</span>
                    </div>
                  </div>
                </div>
                {g.description && (
                  <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4, marginBottom: 10, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {g.description}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#64748b' }}>
                  <span><strong style={{ color: '#1e293b' }}>{g.userCount}</strong> membres</span>
                  <span><strong style={{ color: '#1e293b' }}>{g.overtimeSettings?.capHoursMonth || DEFAULT_GROUP.overtimeSettings.capHoursMonth}h</strong> cap</span>
                  {g.leaveDefaults?.VA != null && <span><strong style={{ color: '#1e293b' }}>{g.leaveDefaults.VA}j</strong> VA défaut</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editTarget && (
        <GroupEditModal
          groupId={editTarget === 'new' ? null : editTarget.id}
          canEdit={editTarget === 'new' ? canCreate : canEdit}
          canEditDroits={canEditDroits}
          canEditHs={canEditHs}
          onClose={() => setEditTarget(null)}
          onSaved={() => { setEditTarget(null); loadGroups(); }}
        />
      )}
    </div>
  );
}

// =====================================================================
// GroupEditModal — modalul cu 8 tab-uri
// =====================================================================
function GroupEditModal({ groupId, canEdit, canEditDroits, canEditHs, onClose, onSaved }) {
  const isCreate = !groupId;
  const [form, setForm] = useState(isCreate ? { ...DEFAULT_GROUP } : null);
  // Réglages HS tels qu'à l'ouverture : envoyés seulement s'ils ont CHANGÉ (sinon un
  // simple renommage réécrivait un plafond modifié entre-temps ailleurs).
  const [reglagesHsInitiaux, setReglagesHsInitiaux] = useState(isCreate ? { ...DEFAULT_GROUP.overtimeSettings } : null);
  const [members, setMembers] = useState([]); // primary members
  const [secondaryMembers, setSecondaryMembers] = useState([]); // secondary members
  // Le manager en place peut être un compte désactivé. On retient qui c'était À
  // L'OUVERTURE de la fiche pour qu'il reste choisissable tant qu'elle est
  // ouverte — sinon, un clic ailleurs le fait disparaître sans retour possible.
  const [managerInitialId, setManagerInitialId] = useState(null);
  // Le manager tel que renvoyé par la fiche du groupe : sans la liste des comptes
  // (lecture limitée), le champ disait « Aucun » alors qu'il est rempli.
  const [managerInitial, setManagerInitial] = useState(null);
  const [allUsers, setAllUsers] = useState([]); // for member picker
  const [allPermissions, setAllPermissions] = useState([]);
  const [groupPermissions, setGroupPermissions] = useState([]);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // Séparé de `error` : un avertissement accompagne une opération RÉUSSIE.
  // Le peindre en rouge dans la bannière d'erreur faisait passer un succès
  // pour un échec, et le premier rechargement raté l'effaçait.
  const [notice, setNotice] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [activeTab, setActiveTab] = useState('identity');

  // Load group + users + permissions
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [usersList, group, permMatrix] = await Promise.all([
          // Un lecteur avec les seuls droits « équipes » n'a pas forcément celui de
          // lister les comptes : la fiche s'ouvre quand même, sans la liste d'ajout.
          listerComptes([]),
          isCreate ? Promise.resolve(null) : groupsApi.get(groupId),
          permsApi.getMatrix().catch(() => ({ permissions: [] })),
        ]);
        if (cancelled) return;
        setAllUsers(usersList);
        setAllPermissions(permMatrix.permissions || []);
        if (group) {
          setGroupPermissions(group.permissions || []);
          const D = DEFAULT_GROUP.overtimeSettings;   // mêmes défauts qu'à la création
          // Valeur telle qu'en base, même inconnue : l'onglet la montre « à corriger »
          // plutôt que d'afficher un défaut qui n'est pas enregistré.
          const reglagesHs = {
            capHoursMonth:    group.overtimeSettings?.capHoursMonth ?? D.capHoursMonth,
            warningThreshold: group.overtimeSettings?.warningThreshold ?? D.warningThreshold,
            resetPolicy:      group.overtimeSettings?.resetPolicy || D.resetPolicy,
            defaultDecision:  group.overtimeSettings?.defaultDecision || D.defaultDecision,
          };
          setForm({
            name:                     group.name || '',
            description:              group.description || '',
            employmentType:           group.employmentType || 'PERMANENT',
            color:                    group.color || '#6366f1',
            icon:                     group.icon || '🏷️',
            isActive:                 group.isActive !== false,
            managerUserId:            group.managerUserId || null,
            defaultRole:              group.defaultRole || 'OPERATOR',
            defaultContractHoursWeek: group.defaultContractHoursWeek ?? 38,
            schedulePattern:          group.schedulePattern || 'FULL_TIME',
            workDaysMask:             group.workDaysMask ?? 31,
            defaultStartTime:         group.defaultStartTime || '08:00',
            defaultEndTime:           group.defaultEndTime || '17:00',
            leaveDefaults:            { ...DEFAULT_GROUP.leaveDefaults, ...(group.leaveDefaults || {}) },
            allowedLeaveTypes:        group.allowedLeaveTypes?.length ? group.allowedLeaveTypes : DEFAULT_GROUP.allowedLeaveTypes,
            modulesEnabled:           { ...DEFAULT_GROUP.modulesEnabled, ...(group.modulesEnabled || {}) },
            notifyManagerWeekly:      !!group.notifyManagerWeekly,
            notifyOvertimeCap:        group.notifyOvertimeCap || 'NONE',
            notifyTSReminder:         !!group.notifyTSReminder,
            leaveReminderDays:        group.leaveReminderDays ?? 3,
            overtimeSettings: reglagesHs,
          });
          setReglagesHsInitiaux(reglagesHs);   // même objet : la comparaison « a changé ? » est juste
          setMembers(group.members || []);
          setSecondaryMembers(group.secondaryMembers || []);
          setManagerInitialId(group.managerUserId || null);
          setManagerInitial(group.manager || null);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [groupId]);

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }
  function updateNested(field, key, value) {
    setForm((prev) => ({ ...prev, [field]: { ...prev[field], [key]: value } }));
  }

  async function handleSave() {
    if (!form.name || form.name.trim().length === 0) {
      setError('Le nom du groupe est obligatoire.');
      setActiveTab('identity');
      return;
    }
    // Réglages HS : contrôlés et envoyés seulement s'ils ont CHANGÉ et que l'on a le
    // droit (un ancien réglage incohérent ne bloque pas un renommage).
    const hsChanges = canEditHs && JSON.stringify(form.overtimeSettings) !== JSON.stringify(reglagesHsInitiaux);
    if (hsChanges && (!(form.overtimeSettings.capHoursMonth > 0) || !(form.overtimeSettings.warningThreshold > 0)
        || form.overtimeSettings.warningThreshold >= form.overtimeSettings.capHoursMonth)) {
      setError('Heures sup. : plafond et seuil doivent être positifs, et le seuil strictement inférieur au plafond.');
      setActiveTab('overtime');
      return;
    }
    // Valeurs connues (miroir de POLITIQUES_REMISE / DECISIONS_DEFAUT) : une ancienne
    // valeur inconnue est dite ici, sur le bon onglet, avant l'envoi.
    if (hsChanges && (!['NEVER', 'YEARLY', 'MONTHLY'].includes(form.overtimeSettings.resetPolicy)
        || !['NONE', 'PAY_ALL', 'CARRY_ALL'].includes(form.overtimeSettings.defaultDecision))) {
      setError('Heures sup. : « Reset périodique » ou « Décision par défaut » a une ancienne valeur inconnue — choisissez une valeur de la liste.');
      setActiveTab('overtime');
      return;
    }
    if (hsChanges && form.overtimeSettings.resetPolicy === 'MONTHLY' && form.overtimeSettings.defaultDecision === 'CARRY_ALL') {
      setError('Heures sup. : remise à zéro chaque mois et « tout reporter » s\'annulent — choisissez l\'un ou l\'autre.');
      setActiveTab('overtime');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      // Réglages HS envoyés seulement s'ils ont changé (et avec le droit — le serveur
      // les ignorerait sinon).
      const { overtimeSettings: _reglagesHs, ...sansHs } = form;   // eslint-disable-line no-unused-vars
      const payload = hsChanges ? form : sansHs;
      if (isCreate) {
        const created = await groupsApi.create(payload);
        setFeedback({ type: 'success', message: `Groupe "${created.name}" créé.` });
        setTimeout(() => onSaved && onSaved(), 600);
      } else {
        await groupsApi.update(groupId, payload);
        setFeedback({ type: 'success', message: 'Enregistré.' });
        setTimeout(() => onSaved && onSaved(), 600);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddMember(userId, mode = 'PRIMARY') {
    try {
      setError(null); setNotice(null);
      const res = await groupsApi.addMember(groupId, userId, mode);
      // Le serveur rétablit automatiquement les droits de base perdus lors
      // d'un changement de groupe. Quand cette réparation échoue, il le dit —
      // encore faut-il l'afficher, sinon l'opérateur voit un succès muet.
      // Posé AVANT le rechargement : si celui-ci échoue, c'est son erreur qui
      // s'affiche — mais l'avertissement reste, il est la seule trace qu'un
      // compte peut être resté muet. Le placer après le faisait disparaître
      // au premier incident réseau.
      if (res?.warning) setNotice(res.warning);
      // Refresh
      const g = await groupsApi.get(groupId);
      setMembers(g.members || []);
      setSecondaryMembers(g.secondaryMembers || []);
      // L'opération a RÉUSSI : un refus de relire la liste ne doit pas afficher
      // une erreur rouge par-dessus. On garde l'ancienne liste.
      try { setAllUsers(await listerComptes(allUsers)); } catch (_) { /* liste non rafraîchie : l'opération, elle, a réussi */ }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur');
    }
  }
  async function handleRemoveMember(userId, mode) {
    if (!confirm(mode === 'SECONDARY' ? 'Retirer cette appartenance secondaire ?' : 'Retirer ce membre principal du groupe ?')) return;
    try {
      setError(null); setNotice(null);
      const res = await groupsApi.removeMember(groupId, userId, mode);
      if (res?.warning) setNotice(res.warning);
      const g = await groupsApi.get(groupId);
      setMembers(g.members || []);
      setSecondaryMembers(g.secondaryMembers || []);
      // L'opération a RÉUSSI : un refus de relire la liste ne doit pas afficher
      // une erreur rouge par-dessus. On garde l'ancienne liste.
      try { setAllUsers(await listerComptes(allUsers)); } catch (_) { /* liste non rafraîchie : l'opération, elle, a réussi */ }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur');
    }
  }

  if (loading || !form) {
    return (
      <Overlay onClose={onClose}>
        {/* Si le chargement a échoué, « Chargement... » pour toujours serait un
            mensonge : on dit ce qui s'est passé et on laisse fermer. */}
        {error && !loading
          ? <div style={{ padding: 40, textAlign: 'center' }}>
              <div style={{ color: '#b91c1c', marginBottom: 14 }}>{error}</div>
              <button onClick={onClose} style={{ padding: '8px 16px', background: 'white', border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Fermer</button>
            </div>
          : <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>Chargement...</div>}
      </Overlay>
    );
  }

  const TABS = [
    { id: 'identity',      label: '🏷️ Identité' },
    { id: 'members',       label: `👥 Membres${!isCreate ? ` (${members.length + (secondaryMembers || []).length})` : ''}` },
    { id: 'contract',      label: '⏰ Contrat' },
    { id: 'overtime',      label: '🔥 Heures sup.' },
    { id: 'leaves',        label: '🌴 Congés défaut' },
    { id: 'permissions',   label: '🔐 Permissions' },
    { id: 'modules',       label: '🧩 Modules' },
    { id: 'notifications', label: '🔔 Notifications' },
  ];

  return (
    <Overlay onClose={onClose}>
      <div style={{ background: 'white', borderRadius: 10, maxWidth: 920, width: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '14px 18px', background: `linear-gradient(135deg, ${form.color || '#6366f1'}, ${shade(form.color || '#6366f1', -20)})`, color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 16, color: 'white' }}>
            {form.icon || '🏷️'} {isCreate ? 'Nouveau groupe' : `Modifier groupe : ${form.name || '...'}`}
          </h3>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 0, width: 28, height: 28, borderRadius: 4, cursor: 'pointer', fontSize: 16 }}>×</button>
        </div>

        {/* Tab bar */}
        <div style={{ display: 'flex', gap: 2, padding: '0 16px', background: '#f1f5f9', borderBottom: '1px solid #cbd5e1', overflowX: 'auto', flexShrink: 0 }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              style={{
                padding: '10px 14px', border: 0, background: activeTab === t.id ? 'white' : 'transparent',
                color: activeTab === t.id ? '#4f46e5' : '#475569', fontSize: 13, cursor: 'pointer',
                whiteSpace: 'nowrap', borderBottom: '2px solid ' + (activeTab === t.id ? '#4f46e5' : 'transparent'),
                marginBottom: -1, fontWeight: activeTab === t.id ? 600 : 400,
              }}
            >{t.label}</button>
          ))}
        </div>

        {/* Body */}
        <div style={{ padding: 18, overflowY: 'auto', flex: 1 }}>
          {error && <div style={{ padding: 10, marginBottom: 12, background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 13 }}>{error}</div>}
          {notice && <div style={{ padding: 10, marginBottom: 12, background: '#fffbeb', color: '#78350f', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 13 }}>{notice}</div>}
          {feedback && <div style={{ padding: 10, marginBottom: 12, background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 6, fontSize: 13 }}>{feedback.message}</div>}

          {activeTab === 'identity'      && <IdentityTab form={form} update={update} disabled={!canEdit} />}
          {activeTab === 'members'       && <MembersTab form={form} update={update} members={members} managerInitialId={managerInitialId} managerInitial={managerInitial} secondaryMembers={secondaryMembers} allUsers={allUsers} isCreate={isCreate} onAddMember={handleAddMember} onRemoveMember={handleRemoveMember} disabled={!canEdit} groupId={groupId} />}
          {activeTab === 'contract'      && <ContractTab form={form} update={update} disabled={!canEdit} />}
          {activeTab === 'overtime'      && <OvertimeTab form={form} updateNested={updateNested} disabled={!canEdit || !canEditHs} reserve={canEdit && !canEditHs} />}
          {activeTab === 'leaves'        && <LeavesTab form={form} update={update} updateNested={updateNested} disabled={!canEdit} />}
          {activeTab === 'permissions'   && <PermissionsTab form={form} update={update} disabled={!canEditDroits} disabledReglages={!canEdit} groupId={groupId} allPermissions={allPermissions} groupPermissions={groupPermissions} setGroupPermissions={setGroupPermissions} />}
          {activeTab === 'modules'       && <ModulesTab form={form} updateNested={updateNested} disabled={!canEdit} />}
          {activeTab === 'notifications' && <NotificationsTab form={form} update={update} disabled={!canEdit} />}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 18px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: 10, background: '#f8fafc', flexShrink: 0 }}>
          <button onClick={onClose} disabled={saving} style={{ padding: '8px 16px', background: 'white', border: '1px solid #cbd5e1', borderRadius: 6, cursor: saving ? 'wait' : 'pointer', fontSize: 13 }}>Annuler</button>
          {canEdit && (
            <button onClick={handleSave} disabled={saving} style={{ padding: '8px 16px', background: saving ? '#94a3b8' : '#16a34a', color: 'white', border: 0, borderRadius: 6, cursor: saving ? 'wait' : 'pointer', fontSize: 13, fontWeight: 500 }}>
              {saving ? 'Enregistrement...' : (isCreate ? 'Créer' : 'Enregistrer')}
            </button>
          )}
        </div>
      </div>
    </Overlay>
  );
}

// =====================================================================
// Tabs
// =====================================================================

function IdentityTab({ form, update, disabled }) {
  return (
    <div>
      <h4 style={tabHeaderStyle}>1. Identité</h4>
      <Field label="Nom du groupe *"><input type="text" value={form.name} onChange={(e) => update('name', e.target.value)} style={inputWide} disabled={disabled} /></Field>
      <Field label="Description"><input type="text" value={form.description || ''} onChange={(e) => update('description', e.target.value)} style={inputWide} disabled={disabled} placeholder="Description courte visible aux managers" /></Field>
      <Field label="Type d'emploi">
        <select value={form.employmentType || ''} onChange={(e) => update('employmentType', e.target.value)} style={selectStyle} disabled={disabled}>
          <option value="">— Aucun —</option>
          {EMPLOYMENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </Field>
      <Field label="Couleur & icône" hint="utilisé dans calendrier et rapports">
        <input type="color" value={form.color || '#6366f1'} onChange={(e) => update('color', e.target.value)} disabled={disabled} style={{ marginRight: 8, width: 44, height: 32, padding: 0, border: '1px solid #cbd5e1', borderRadius: 4, cursor: 'pointer' }} />
        <input type="text" value={form.icon || ''} onChange={(e) => update('icon', e.target.value)} disabled={disabled} style={{ width: 50, textAlign: 'center', padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 16 }} placeholder="📦" />
      </Field>
      <Field label="Statut">
        <Toggle checked={form.isActive} onChange={(v) => update('isActive', v)} disabled={disabled} label={form.isActive ? 'Actif' : 'Archivé'} />
      </Field>
    </div>
  );
}

function MembersTab({ form, update, members, secondaryMembers, allUsers, isCreate, onAddMember, onRemoveMember, disabled, groupId, managerInitialId, managerInitial }) {
  // Les groupes d'un ACCÈS TOTAL ne changent que par un accès total (serveur) : pas de
  // bouton voué au refus.
  const { user: moi } = useAuth();
  const intouchable = (m) => ficheReservee(m, moi);
  const [picker, setPicker] = useState('');
  const [addMode, setAddMode] = useState('PRIMARY'); // PRIMARY | SECONDARY

  const primaryIds = new Set((members || []).map((m) => m.id));
  const secondaryIds = new Set((secondaryMembers || []).map((m) => m.id));

  // Eligible for PRIMARY: any active user not already PRIMARY here
  // Eligible for SECONDARY: any active user not already SECONDARY AND not already PRIMARY here
  // 03-10-2026 : demande explicite — les personnes se lisent par ordre
  // alphabétique partout, y compris ici où la liste était groupée par équipe.
  // Mémorisé : ce composant se redessine à chaque frappe dans la fiche du
  // groupe, et re-trier une centaine de personnes à chaque touche se sent.
  const [sansEquipe, avecEquipe] = useMemo(() => {
    // `allUsers` arrive déjà trié du serveur et `filter` garde l'ordre.
    const eligibles = allUsers.filter((u) => {
      if (!u.isActive) return false;
      if (intouchable(u)) return false;   // accès total : pas proposé (refusé par le serveur)
      if (addMode === 'PRIMARY')   return !primaryIds.has(u.id);
      /* SECONDARY */              return !secondaryIds.has(u.id) && !primaryIds.has(u.id);
    });
    return [eligibles.filter((u) => !u.group), eligibles.filter((u) => u.group)];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allUsers, addMode, members, secondaryMembers, moi]);
  const nbEligibles = sansEquipe.length + avecEquipe.length;

  return (
    <div>
      <h4 style={tabHeaderStyle}>2. Membres</h4>
      {isCreate && (
        <div style={{ padding: 12, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, color: '#78350f', fontSize: 13, marginBottom: 12 }}>
          ⓘ Sauvegardez d'abord le groupe, puis ajoutez des membres depuis la liste.
        </div>
      )}
      {!isCreate && (
        <>
          <Field label={`Membres principaux (${members.length})`} hint="User.groupId — groupe d'identité (employment type)">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 8, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, minHeight: 40, width: '100%' }}>
              {members.length === 0 ? <span style={{ color: '#94a3b8', fontSize: 12 }}>Aucun membre principal</span> :
                members.map((m) => (
                  <span key={m.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: 'white', border: '1px solid #cbd5e1', borderRadius: 12, fontSize: 11 }}>
                    {m.name}{marqueurInactif(m)} <span style={{ color: '#94a3b8' }}>({libelleRole(m.role)})</span>
                    {!disabled && !intouchable(m) && <button onClick={() => onRemoveMember(m.id, 'PRIMARY')} style={{ background: 'none', border: 0, color: '#dc2626', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>}
                  </span>
                ))
              }
            </div>
          </Field>

          <Field label={`Membres secondaires (${(secondaryMembers || []).length})`} hint="Appartenance additionnelle : projet, équipe transverse">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 8, background: '#faf5ff', border: '1px dashed #c4b5fd', borderRadius: 6, minHeight: 40, width: '100%' }}>
              {(secondaryMembers || []).length === 0 ? <span style={{ color: '#94a3b8', fontSize: 12 }}>Aucun membre secondaire</span> :
                (secondaryMembers || []).map((m) => (
                  <span key={'sec_' + m.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', background: 'white', border: '1px dashed #c4b5fd', borderRadius: 12, fontSize: 11 }}>
                    <span style={{ color: '#7c3aed', fontWeight: 600, fontSize: 9 }}>SECONDAIRE</span> {m.name}{marqueurInactif(m)} <span style={{ color: '#94a3b8' }}>({libelleRole(m.role)})</span>
                    {!disabled && !intouchable(m) && <button onClick={() => onRemoveMember(m.id, 'SECONDARY')} style={{ background: 'none', border: 0, color: '#dc2626', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>}
                  </span>
                ))
              }
            </div>
          </Field>

          {!disabled && (
            <Field label="Ajouter un membre" hint={addMode === 'PRIMARY' ? "déplacera depuis son groupe principal actuel" : "appartenance additionnelle, garde son groupe principal"}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
                <div style={{ display: 'inline-flex', gap: 4, background: '#f1f5f9', padding: 2, borderRadius: 6 }}>
                  <button onClick={() => setAddMode('PRIMARY')} style={{ padding: '4px 10px', border: 0, borderRadius: 4, background: addMode === 'PRIMARY' ? 'white' : 'transparent', color: addMode === 'PRIMARY' ? '#1e293b' : '#64748b', fontSize: 11, fontWeight: addMode === 'PRIMARY' ? 600 : 400, cursor: 'pointer' }}>Principal</button>
                  <button onClick={() => setAddMode('SECONDARY')} style={{ padding: '4px 10px', border: 0, borderRadius: 4, background: addMode === 'SECONDARY' ? 'white' : 'transparent', color: addMode === 'SECONDARY' ? '#7c3aed' : '#64748b', fontSize: 11, fontWeight: addMode === 'SECONDARY' ? 600 : 400, cursor: 'pointer' }}>Secondaire</button>
                </div>
                <select value={picker} onChange={(e) => {
                  if (!e.target.value) return;
                  const u = allUsers.find((x) => x.id === e.target.value);
                  if (addMode === 'PRIMARY' && u && u.group && !confirm(`${u.name} est actuellement dans le groupe principal "${u.group.name}". Le déplacer vers ce groupe ?`)) {
                    setPicker('');
                    return;
                  }
                  onAddMember(e.target.value, addMode);
                  setPicker('');
                }} style={{ ...selectStyle, flex: 1, minWidth: 280 }}>
                  <option value="">{allUsers.refusee
                    ? "Vous n'avez pas le droit de lister les comptes — demandez à un administrateur"
                    : nbEligibles === 0 ? 'Aucun utilisateur disponible' : '— Choisir un utilisateur —'}</option>
                  {/* Deux sections, alphabétiques chacune : « sans équipe » d'abord,
                      parce qu'ajouter quelqu'un, c'est presque toujours ajouter
                      quelqu'un qui n'en a pas encore. L'ordre alphabétique seul
                      noyait cette personne au milieu de la centaine d'autres. */}
                  {[
                    ['Sans équipe', sansEquipe],
                    ['Déjà dans une équipe', avecEquipe],
                  ].filter(([, liste]) => liste.length > 0).map(([titre, liste]) => (
                    <optgroup key={titre} label={titre}>
                      {liste.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.username}) · {libelleRole(u.role)}
                          {u.group ? ` ← ${u.group.name}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            </Field>
          )}
        </>
      )}
      <Field label="Manager du groupe" hint="reçoit notifications hebdo + dashboard">
        <select value={form.managerUserId || ''} onChange={(e) => update('managerUserId', e.target.value || null)} style={selectStyle} disabled={disabled}>
          <option value="">{allUsers.refusee && !managerInitial
            ? "— Aucun — (vous n'avez pas le droit de lister les comptes)"
            : '— Aucun —'}</option>
          {allUsers.filter((u) => u.isActive || u.id === managerInitialId)
            .map((u) => <option key={u.id} value={u.id}>{u.name}{u.role ? ` (${libelleRole(u.role)})` : ''}{marqueurInactif(u)}</option>)}
          {managerInitial && !allUsers.some((u) => u.id === managerInitial.id) && (
            <option value={managerInitial.id}>{managerInitial.name}{marqueurInactif(managerInitial)}</option>
          )}
        </select>
      </Field>
      <Field label="Rôle par défaut" hint="auto-set pour nouveaux membres">
        <select value={form.defaultRole || ''} onChange={(e) => update('defaultRole', e.target.value)} style={selectStyle} disabled={disabled}>
          <option value="">— Aucun —</option>
          {ROLES.map((r) => <option key={r} value={r}>{libelleRole(r)}</option>)}
        </select>
      </Field>
    </div>
  );
}

function ContractTab({ form, update, disabled }) {
  const days = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
  function toggleDay(idx) {
    const bit = 1 << idx;
    const newMask = (form.workDaysMask & bit) ? (form.workDaysMask & ~bit) : (form.workDaysMask | bit);
    update('workDaysMask', newMask);
  }
  return (
    <div>
      <h4 style={tabHeaderStyle}>3. Contrat & horaires</h4>
      <Field label="Heures contractuelles / semaine" hint="défaut pour nouveaux membres">
        <input type="number" min="0" max="80" step="0.5" value={form.defaultContractHoursWeek ?? ''} onChange={(e) => update('defaultContractHoursWeek', parseFloat(e.target.value) || 0)} disabled={disabled} style={{ ...inputStyle, width: 100 }} /> <span style={{ color: '#64748b', fontSize: 12 }}>h</span>
      </Field>
      <Field label="Pattern horaire">
        <select value={form.schedulePattern || ''} onChange={(e) => update('schedulePattern', e.target.value)} style={selectStyle} disabled={disabled}>
          <option value="">— Aucun —</option>
          {SCHEDULE_PATTERNS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
      </Field>
      <Field label="Jours travaillés">
        <div style={{ display: 'flex', gap: 6 }}>
          {days.map((d, i) => (
            <label key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, cursor: disabled ? 'default' : 'pointer', fontSize: 13 }}>
              <input type="checkbox" checked={!!(form.workDaysMask & (1 << i))} onChange={() => toggleDay(i)} disabled={disabled} />
              {d}
            </label>
          ))}
        </div>
      </Field>
      <Field label="Heure début défaut">
        <input type="text" value={form.defaultStartTime || ''} onChange={(e) => update('defaultStartTime', e.target.value)} disabled={disabled} style={{ ...inputStyle, width: 100 }} placeholder="08:00" />
      </Field>
      <Field label="Heure fin défaut">
        <input type="text" value={form.defaultEndTime || ''} onChange={(e) => update('defaultEndTime', e.target.value)} disabled={disabled} style={{ ...inputStyle, width: 100 }} placeholder="17:00" />
      </Field>
    </div>
  );
}

function OvertimeTab({ form, updateNested, disabled, reserve }) {
  const o = form.overtimeSettings;
  return (
    <div>
      <h4 style={tabHeaderStyle}>4. Heures supplémentaires</h4>
      {reserve && (
        <p style={{ fontSize: 12, color: '#b45309', marginTop: -4 }}>
          Lecture seule : les réglages d'heures sup. d'une équipe sont réservés au droit « Heures sup. ». Demandez à un administrateur.
        </p>
      )}
      <Field label="Plafond mensuel">
        <input type="number" min="0.5" step="0.5" value={o.capHoursMonth} onChange={(e) => updateNested('overtimeSettings', 'capHoursMonth', parseFloat(e.target.value) || 0)} disabled={disabled} style={{ ...inputStyle, width: 100 }} /> <span style={{ color: '#64748b', fontSize: 12 }}>h</span>
      </Field>
      <Field label="Seuil d'avertissement">
        <input type="number" min="0.5" step="0.5" value={o.warningThreshold} onChange={(e) => updateNested('overtimeSettings', 'warningThreshold', parseFloat(e.target.value) || 0)} disabled={disabled} style={{ ...inputStyle, width: 100 }} /> <span style={{ color: '#64748b', fontSize: 12 }}>h</span>
      </Field>
      <Field label="Reset périodique" hint="⚠️ enregistré, mais pas encore appliqué automatiquement">
        <select value={o.resetPolicy || 'NEVER'} onChange={(e) => updateNested('overtimeSettings', 'resetPolicy', e.target.value)} style={selectStyle} disabled={disabled}>
          <option value="NEVER">Jamais (cumulatif perpétuel)</option>
          <option value="YEARLY">Chaque 1er janvier</option>
          {!['NEVER', 'YEARLY', 'MONTHLY'].includes(o.resetPolicy) && <option value={o.resetPolicy}>Valeur inconnue « {o.resetPolicy} » — à corriger</option>}
          <option value="MONTHLY" disabled={o.defaultDecision === 'CARRY_ALL'}>Chaque 1er du mois{o.defaultDecision === 'CARRY_ALL' ? ' — incompatible avec « tout reporter »' : ''}</option>
        </select>
      </Field>
      <Field label="Décision par défaut" hint="⚠️ enregistrée, mais pas encore appliquée automatiquement">
        <select value={o.defaultDecision || 'NONE'} onChange={(e) => updateNested('overtimeSettings', 'defaultDecision', e.target.value)} style={selectStyle} disabled={disabled}>
          <option value="NONE">Aucune (admin décide)</option>
          <option value="PAY_ALL">Tout payer automatiquement</option>
          {!['NONE', 'PAY_ALL', 'CARRY_ALL'].includes(o.defaultDecision) && <option value={o.defaultDecision}>Valeur inconnue « {o.defaultDecision} » — à corriger</option>}
          <option value="CARRY_ALL" disabled={o.resetPolicy === 'MONTHLY'}>Tout reporter automatiquement{o.resetPolicy === 'MONTHLY' ? ' — incompatible avec la remise à zéro mensuelle' : ''}</option>
        </select>
      </Field>
    </div>
  );
}

function LeavesTab({ form, update, updateNested, disabled }) {
  const allowed = form.allowedLeaveTypes || [];
  function toggleType(code) {
    const next = allowed.includes(code) ? allowed.filter((c) => c !== code) : [...allowed, code];
    update('allowedLeaveTypes', next);
  }
  return (
    <div>
      <h4 style={tabHeaderStyle}>5. Quotas congés par défaut</h4>
      <p style={{ fontSize: 12, color: '#64748b', marginTop: 0 }}>Valeurs initiales utilisées lors de la création des soldes annuels pour un nouveau membre. Editables individuellement après dans "Quotas annuels".</p>
      {LEAVE_TYPES.map((t) => (
        <Field key={t.code} label={`${t.code} — ${t.label}`}>
          <input type="number" min="0" max="365" value={form.leaveDefaults?.[t.code] ?? 0} onChange={(e) => updateNested('leaveDefaults', t.code, parseFloat(e.target.value) || 0)} disabled={disabled || t.code === 'REC' || t.code === 'MAL'} style={{ ...inputStyle, width: 90 }} />
          <span style={{ color: '#64748b', fontSize: 12, marginLeft: 6 }}>j</span>
          {(t.code === 'REC' || t.code === 'MAL') && <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8' }}>(auto-géré)</span>}
        </Field>
      ))}
      <h4 style={{ ...tabHeaderStyle, marginTop: 18 }}>Types autorisés pour ce groupe</h4>
      <Field label="Types visibles aux membres" hint="décocher = masqué du dropdown demande">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {LEAVE_TYPES.map((t) => (
            <label key={t.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, cursor: disabled ? 'default' : 'pointer' }}>
              <input type="checkbox" checked={allowed.includes(t.code)} onChange={() => toggleType(t.code)} disabled={disabled} />
              {t.code}
            </label>
          ))}
        </div>
      </Field>
    </div>
  );
}

// ---------------------------------------------------------------------
// Permission templates — preset configurations for quick group setup.
// Each template returns a list of permission IDs to grant to the group.
// Note: only IDs that exist in the DB are kept (frontend filters at apply time).
// ---------------------------------------------------------------------
const PERMISSION_TEMPLATES = {
  operator_self: {
    label: '👤 Opérateur standard (Propres uniquement)',
    description: 'Voit et modifie uniquement ses propres données — pas de visibilité sur les autres.',
    permissions: [
      'leaves.read.own', 'leaves.create.own',
      'timesheets.read.own', 'timesheets.create.own', 'timesheets.update.own', 'timesheets.delete.own',
      'presence.read.own', 'presence.update.own',
      'overtime.read.own',
      'calendar.read.own',
      'dashboards.read.own',
      'evaluations.read.own',
    ],
  },
  operator_team: {
    label: '👥 Opérateur + voir équipe',
    description: 'Opérateur standard + accès en lecture aux données de ses collègues du même groupe.',
    permissions: [
      'leaves.read.own', 'leaves.create.own',
      'timesheets.read.own', 'timesheets.create.own', 'timesheets.update.own', 'timesheets.delete.own',
      'presence.read.own', 'presence.update.own',
      'overtime.read.own',
      'calendar.read.own',
      'dashboards.read.own',
      'evaluations.read.own',
      // Team-level reads
      'timesheets.read.group',
      'presence.read.group',
    ],
  },
  sav: {
    label: '🛠️ SAV (modules SAV + propres)',
    description: 'Opérateur SAV : ses propres données + accès aux modules SAV (suivi clients).',
    permissions: [
      'leaves.read.own', 'leaves.create.own',
      'timesheets.read.own', 'timesheets.create.own', 'timesheets.update.own', 'timesheets.delete.own',
      'presence.read.own', 'presence.update.own',
      'overtime.read.own',
      'calendar.read.own',
      'dashboards.read.own',
      'contacts.read.all', 'contacts.update.all',
    ],
  },
  coordinator: {
    label: '📅 Coordinator (validation TS)',
    description: 'Coordinator : opérateur standard + validation timesheets équipe + voir tous les congés.',
    permissions: [
      'leaves.read.own', 'leaves.create.own', 'leaves.read.all',
      'timesheets.read.own', 'timesheets.create.own', 'timesheets.update.own', 'timesheets.delete.own',
      'timesheets.read.group', 'timesheets.approve.group',
      'presence.read.own', 'presence.update.own', 'presence.read.group',
      'overtime.read.own', 'overtime.read.all',
      'calendar.read.own', 'calendar.read.all',
      'dashboards.read.own', 'dashboards.read.all',
      'users.read.all',
    ],
  },
  admin_all: {
    label: '🛡️ Admin (accès complet)',
    description: 'Accès complet à toutes les fonctionnalités (similar SUPER_ADMIN mais sans le bypass).',
    permissions: null, // signal: take all "*.all" + "*.own" permissions from DB
  },
};

function PermissionsTab({ form, update, disabled, disabledReglages, groupId, allPermissions, groupPermissions, setGroupPermissions }) {
  const [search, setSearch] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [saving, setSaving] = useState(false);

  const granted = useMemo(() => new Set(groupPermissions || []), [groupPermissions]);

  // Group permissions by category for display
  const byCategory = useMemo(() => {
    const result = {};
    for (const p of allPermissions) {
      if (!result[p.category]) result[p.category] = [];
      result[p.category].push(p);
    }
    // sort
    for (const cat in result) {
      result[cat].sort((a, b) => a.id.localeCompare(b.id));
    }
    return result;
  }, [allPermissions]);

  const filteredCategories = useMemo(() => {
    if (!search.trim()) return byCategory;
    const q = search.trim().toLowerCase();
    const out = {};
    for (const [cat, perms] of Object.entries(byCategory)) {
      const matching = perms.filter((p) => p.id.includes(q) || (p.label || '').toLowerCase().includes(q) || cat.toLowerCase().includes(q));
      if (matching.length > 0) out[cat] = matching;
    }
    return out;
  }, [byCategory, search]);

  function applyTemplate(key) {
    const tpl = PERMISSION_TEMPLATES[key];
    if (!tpl) return;
    let permIds;
    if (tpl.permissions === null) {
      // admin_all: take everything available
      permIds = allPermissions.map((p) => p.id);
    } else {
      // Filter to only IDs that exist in DB
      const existing = new Set(allPermissions.map((p) => p.id));
      permIds = tpl.permissions.filter((id) => existing.has(id));
    }
    setGroupPermissions(permIds);
    setFeedback({ type: 'success', message: `Template "${tpl.label}" appliqué (${permIds.length} permissions). N'oubliez pas d'enregistrer.` });
  }

  function togglePermission(permId) {
    const next = new Set(granted);
    if (next.has(permId)) next.delete(permId);
    else next.add(permId);
    setGroupPermissions(Array.from(next));
  }

  function selectAllInCategory(category) {
    const next = new Set(granted);
    for (const p of byCategory[category] || []) next.add(p.id);
    setGroupPermissions(Array.from(next));
  }
  function clearCategory(category) {
    const next = new Set(granted);
    for (const p of byCategory[category] || []) next.delete(p.id);
    setGroupPermissions(Array.from(next));
  }

  async function handleSavePermissions() {
    if (!groupId) {
      setFeedback({ type: 'error', message: 'Sauvegardez d\'abord le groupe avant de gérer les permissions.' });
      return;
    }
    setSaving(true);
    setFeedback(null);
    try {
      await groupsApi.setPermissions(groupId, Array.from(granted));
      setFeedback({ type: 'success', message: `Permissions sauvegardées (${granted.size} actives).` });
    } catch (e) {
      setFeedback({ type: 'error', message: e?.response?.data?.message || e.message || 'Erreur' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <h4 style={tabHeaderStyle}>6. Permissions par groupe</h4>
      <div style={{ padding: 10, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, color: '#1e3a8a', fontSize: 12, marginBottom: 12 }}>
        ⓘ Permissions effectives d'un membre = (rôle) ∪ (groupe ci-dessous) ∪ (override grant) − (override deny).<br/>
        Pour overrides individuels, voir <a href="/admin/permissions" style={{ color: '#4f46e5' }}>/admin/permissions → Overrides</a>.
      </div>

      <Field label="Rôle par défaut" hint="auto-attribué aux nouveaux membres">
        <select value={form.defaultRole || ''} onChange={(e) => update('defaultRole', e.target.value)} style={selectStyle} disabled={disabledReglages}>
          <option value="">— Aucun —</option>
          {ROLES.map((r) => <option key={r} value={r}>{libelleRole(r)}</option>)}
        </select>
      </Field>

      {/* Templates rapides */}
      <div style={{ marginTop: 14, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6 }}>
        <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>⚡ Templates rapides (configuration automatique)</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {Object.entries(PERMISSION_TEMPLATES).map(([key, tpl]) => (
            <button
              key={key}
              onClick={() => applyTemplate(key)}
              disabled={disabled}
              title={tpl.description}
              style={{ padding: '6px 10px', background: 'white', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}
            >{tpl.label}</button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>
          Click sur un template → matrice ci-dessous mise à jour. N'oubliez pas d'enregistrer.
        </div>
      </div>

      {feedback && (
        <div style={{ marginTop: 10, padding: 8, borderRadius: 6, fontSize: 12,
          background: feedback.type === 'success' ? '#dcfce7' : '#fee2e2',
          color: feedback.type === 'success' ? '#166534' : '#991b1b',
          border: '1px solid ' + (feedback.type === 'success' ? '#86efac' : '#fca5a5'),
        }}>{feedback.message}</div>
      )}

      {/* Matrice avansée (expansible) */}
      <details style={{ marginTop: 14 }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, color: '#4f46e5', fontSize: 13, padding: '6px 0' }}>
          ▶ Personnaliser manuellement (matrice détaillée : {granted.size} active{granted.size > 1 ? 's' : ''} / {allPermissions.length} total)
        </summary>
        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
            <input
              type="text"
              placeholder="🔍 Filtrer par ID, label ou catégorie..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ ...inputStyle, flex: 1, maxWidth: 320 }}
            />
            <span style={{ fontSize: 11, color: '#64748b' }}>{Object.values(filteredCategories).flat().length} permissions affichées</span>
          </div>

          {Object.keys(filteredCategories).sort().map((cat) => {
            const perms = filteredCategories[cat];
            const allOn = perms.every((p) => granted.has(p.id));
            return (
              <div key={cat} style={{ marginBottom: 12, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' }}>
                <div style={{ padding: '6px 10px', background: '#f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                  <span style={{ fontWeight: 600, color: '#1e40af' }}>{cat.toUpperCase()} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({perms.length})</span></span>
                  {!disabled && (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => selectAllInCategory(cat)} style={catBtnStyle}>Tout ✓</button>
                      <button onClick={() => clearCategory(cat)} style={catBtnStyle}>Tout ✗</button>
                    </div>
                  )}
                </div>
                {perms.map((p) => {
                  const isOn = granted.has(p.id);
                  return (
                    <div
                      key={p.id}
                      onClick={() => !disabled && togglePermission(p.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '6px 10px', borderTop: '1px solid #f1f5f9',
                        cursor: disabled ? 'default' : 'pointer',
                        background: isOn ? '#f0fdf4' : 'white',
                      }}
                    >
                      <input type="checkbox" checked={isOn} onChange={() => {}} disabled={disabled} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontFamily: 'monospace', color: isOn ? '#166534' : '#475569' }}>{p.id}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{p.label || ''}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </details>

      {!disabled && groupId && (
        <div style={{ marginTop: 14, padding: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#475569' }}>
            <strong>{granted.size}</strong> permission{granted.size > 1 ? 's' : ''} active{granted.size > 1 ? 's' : ''} pour ce groupe
          </span>
          <button
            onClick={handleSavePermissions}
            disabled={saving}
            style={{ padding: '6px 14px', background: saving ? '#94a3b8' : '#4f46e5', color: 'white', border: 0, borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: saving ? 'wait' : 'pointer' }}
          >
            {saving ? 'Sauvegarde...' : '💾 Sauvegarder permissions du groupe'}
          </button>
        </div>
      )}
    </div>
  );
}

const catBtnStyle = { padding: '2px 8px', background: 'white', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 10, cursor: 'pointer' };

function ModulesTab({ form, updateNested, disabled }) {
  return (
    <div>
      <h4 style={tabHeaderStyle}>7. Modules activés pour ce groupe</h4>
      <p style={{ fontSize: 12, color: '#64748b', marginTop: 0 }}>Si désactivé, le module disparaît du menu pour tous les membres du groupe. <em>Note : l'application du masquage UI requiert une lecture côté frontend ; pour l'instant ce paramètre est stocké et exposé via l'API.</em></p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
        {MODULES.map((m) => {
          const enabled = !!(form.modulesEnabled?.[m.key]);
          return (
            <div key={m.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
              <span>{m.label}</span>
              <Toggle checked={enabled} onChange={(v) => updateNested('modulesEnabled', m.key, v)} disabled={disabled} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NotificationsTab({ form, update, disabled }) {
  return (
    <div>
      <h4 style={tabHeaderStyle}>8. Notifications & communication</h4>
      <div style={{ padding: 10, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, color: '#1e3a8a', fontSize: 12, marginBottom: 14 }}>
        ⓘ Les valeurs sont enregistrées dès maintenant ; l'envoi email réel nécessite l'infra SMTP (à configurer séparément).
      </div>
      <Field label="Digest hebdomadaire au manager" hint="résumé activité du groupe par email">
        <Toggle checked={form.notifyManagerWeekly} onChange={(v) => update('notifyManagerWeekly', v)} disabled={disabled} />
      </Field>
      <Field label="Alerte plafond sup. atteint">
        <select value={form.notifyOvertimeCap || 'NONE'} onChange={(e) => update('notifyOvertimeCap', e.target.value)} style={selectStyle} disabled={disabled}>
          <option value="NONE">Désactivé</option>
          <option value="EMAIL_MANAGER">Email manager seul</option>
          <option value="EMAIL_BOTH">Email manager + concerné</option>
        </select>
      </Field>
      <Field label="Rappel TS non soumis" hint="vendredi soir si TS de la semaine pas envoyé">
        <Toggle checked={form.notifyTSReminder} onChange={(v) => update('notifyTSReminder', v)} disabled={disabled} />
      </Field>
      <Field label="Délai relance congé en attente" hint="rappel admin si pas validée après X jours">
        <input type="number" min="1" max="30" value={form.leaveReminderDays ?? 3} onChange={(e) => update('leaveReminderDays', parseInt(e.target.value, 10) || 3)} disabled={disabled} style={{ ...inputStyle, width: 70 }} /> <span style={{ color: '#64748b', fontSize: 12, marginLeft: 6 }}>jours</span>
      </Field>
    </div>
  );
}

// =====================================================================
// Helpers UI
// =====================================================================

function Overlay({ children, onClose }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: '100%', maxWidth: 920 }}>{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 14, padding: '10px 0', borderBottom: '1px solid #f1f5f9', alignItems: 'center' }}>
      <label style={{ fontSize: 13, color: '#475569', fontWeight: 500 }}>
        {label}
        {hint && <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 400, marginTop: 2 }}>{hint}</div>}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>{children}</div>
    </div>
  );
}

function Toggle({ checked, onChange, disabled, label }) {
  return (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: disabled ? 'default' : 'pointer', fontSize: 13 }}>
      <span style={{ width: 38, height: 20, background: checked ? '#16a34a' : '#cbd5e1', borderRadius: 10, position: 'relative', transition: '0.2s' }}>
        <span style={{ position: 'absolute', top: 2, left: checked ? 20 : 2, width: 16, height: 16, background: 'white', borderRadius: '50%', transition: '0.2s' }}></span>
      </span>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} style={{ display: 'none' }} />
      {label && <span style={{ color: '#475569' }}>{label}</span>}
    </label>
  );
}

// Darken a hex color by N% (negative = darker)
function shade(hex, pct) {
  const num = parseInt(hex.replace('#', ''), 16);
  let r = (num >> 16) + Math.round(255 * pct / 100);
  let g = ((num >> 8) & 0xff) + Math.round(255 * pct / 100);
  let b = (num & 0xff) + Math.round(255 * pct / 100);
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  return '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
}

// =====================================================================
// Inline styles
// =====================================================================

const inputStyle = { padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: 'white' };
const inputWide  = { ...inputStyle, minWidth: 280, width: '100%', maxWidth: 400 };
const selectStyle = { ...inputStyle, minWidth: 180 };
const badgeStyle = { display: 'inline-block', padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600 };
const tabHeaderStyle = { fontSize: 14, margin: '0 0 12px', color: '#1e293b', fontWeight: 600 };
